import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useLocalStorage } from "@vueuse/core";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

import type { AddPortfolioResponse } from "@tmi/types/api/fetch";
import { goFetch, isSuccessState } from "@/utils/network-utils";
import { clientStateManager } from "@/services/error-services";
import type { MyPortfolio, MyPortfolios, MyPosition } from "@tmi/types/api/my-portfolios";
import { Unauthorized } from "@tmi/types/api/responses";
import { RuntimeException } from "@tmi/types/errors";

import { useAuthStore } from "@/stores/Auth";
import { useUserStore } from "@/stores/User";
import { createLoadStatus } from "@/stores/load-status";

/**
 * tmi-e7w.69: how long a successful load satisfies later initialize() calls. Several places make sure portfolios are
 * loaded (Application start, the Portfolio Builder page and two of its components), and each call used to fetch again
 * unless it happened to overlap a request in flight, so one page load sent the query twice. A later visit, after the
 * window, still refreshes; refreshPortfolios() always does.
 */
export const MY_PORTFOLIOS_FRESH_MS = 60_000;

// State values
const isLoading = computed< boolean >( () => loading.value );
const loading = ref< boolean >( false );
// When the last successful load landed (Date.now()), or null when nothing is loaded for this session.
let loadedAtMs: number | null = null;
// The load in flight, so concurrent callers wait for it instead of returning before its data lands.
let inFlightLoad: Promise< void > | null = null;
// Bumped by reset(): a load started before a reset (sign-out, refreshPortfolios) must not apply its response after it.
let loadGeneration = 0;
// errors-4: a failed load emptied the store, so the Portfolio Builder said "This portfolio has no positions yet."
const portfoliosLoad = createLoadStatus();

const defaultPortfolios: MyPortfolio[] = [];
const allPortfolios = useLocalStorage< MyPortfolio[] >( 'my-portfolios', defaultPortfolios );
const selectedPortfolioId = useLocalStorage< number | null >( 'selected-portfolio', null );
const selectedPortfolio = computed< MyPortfolio | undefined >( () => {
    let portfolio = allPortfolios.value.length > 0 ? allPortfolios.value[ 0 ] : undefined;
    if ( !!( selectedPortfolioId.value ) ) {
        portfolio = allPortfolios.value.find( ( portfolio: MyPortfolio ) => portfolio.portfolio_id === selectedPortfolioId.value );
    }

    selectedPortfolioId.value = portfolio?.portfolio_id;

    return portfolio;
} );

export const useMyPortfoliosStore = defineStore( 'my-portfolios', () => {
    const { authToken, isAuthenticated } = storeToRefs( useAuthStore() );
    const userStore = useUserStore();

    function reset(): void {
        // Clear out the local storage value
        allPortfolios.value = null;
        selectedPortfolioId.value = null;
        loading.value = false;
        loadedAtMs = null;
        inFlightLoad = null;
        loadGeneration += 1;
        portfoliosLoad.reset();

        // Reset the local storage variable to the "uninitialized" value
        allPortfolios.value = defaultPortfolios;
    }

    async function initialize(): Promise< void > {
        // If not authenticated or the application context has not yet been created, return immediately.
        // tmi-ck2 C4 signed-out guard: `authToken` is checked alongside `isAuthenticated` because
        // `sentient/my-portfolios` carries .RequireAuthorization and 401s a token-less caller,
        // which goFetch REJECTS. Under GraphQL that same call came back 200 with an `errors`
        // array; here the rejection lands in the catch below, which calls reset() and clears the
        // user's cached portfolios.
        if ( !isAuthenticated.value || !authToken.value ) {
            logger.debug( '[ MyPortfolio ].initialize() exiting - not ready yet...: [ not authenticated => ', !isAuthenticated.value, ' ]' );
            return;
        }

        if ( inFlightLoad ) {
            return inFlightLoad;
        }

        if ( loadedAtMs !== null && Date.now() - loadedAtMs < MY_PORTFOLIOS_FRESH_MS ) {
            return;
        }

        const thisLoad: Promise< void > = load( loadGeneration ).finally( () => {
            if ( inFlightLoad === thisLoad ) {
                inFlightLoad = null;
            }
        } );
        inFlightLoad = thisLoad;
        return thisLoad;
    }

    async function load( generation: number ): Promise< void > {
        const isCurrent = () => generation === loadGeneration;
        loading.value = true;
        portfoliosLoad.start();

        try {
            // tmi-ck2 C4: `myPortfolios` (was persisted query 024e15d4840a5f62f988928cb8450caa).
            // `skip` and `limit` are the GraphQL ARGUMENT names verbatim and their values are the
            // same constants the persisted document's variables carried.
            // DP-14 (tmi-e7w.14): positions keep `metrics.sectors` exactly as the twin sends it. A twin that predates
            // the field omits it, and helpers/position-sectors.ts falls back to the single-sector fields, so unlike the
            // GraphQL document pair there is nothing to retry.
            const result = await goFetch< MyPortfolios, never >( {
                serverBaseUri: `${ import.meta.env.VITE_SENTIENT_BASE_URI }`,
                endpoint: 'sentient/my-portfolios?skip=0&limit=6',
                verbOverride: 'GET',
                authToken: authToken.value,
                // Rejects instead of resolving null on a timeout or a dropped connection, so the failure is recorded.
                forceThrowExceptions: true
            } );

            // A reset happened while this request was out: a newer load (or a sign-out) owns the state now.
            if ( !isCurrent() ) {
                return;
            }

            if ( result?.data ) {
                if ( result.data?.myPortfolios?.portfolios?.length > 0 ) {
                    allPortfolios.value = [ ...result?.data?.myPortfolios?.portfolios ?? [] ];
                    loadedAtMs = Date.now();
                } else {
                    try {
                        // If the user does not have any portfolios, create a "default" portfolio and send it to the server
                        const defaultPortfolioName = `My First Portfolio`;
                        const response =
                            await goFetch< AddPortfolioResponse, { userId: number | null, name: string } >( {
                                endpoint: 'user/add-portfolio',
                                authToken: authToken.value,
                                payload: {
                                    userId: userStore.userId(),
                                    name: defaultPortfolioName
                                }
                            } );

                        // Update the local representation only if there are no errors and the id was returned as confirmation
                        if ( response?.data?.addPortfolio?.id && isSuccessState( response?.status ) ) {
                            const newPortfolioId = response.data.addPortfolio.id;
                            addNewPortfolio( {
                                portfolio_id: newPortfolioId,
                                portfolio_name: defaultPortfolioName,
                                positions: []
                            } );
                            loadedAtMs = Date.now();
                        }
                    } catch( err: any ) {
                        logger.error( JSON.stringify( err.value?.message ?? 'Unknown error occurred' ) );
                    }
                }
            }
            portfoliosLoad.succeed();
        } catch ( err: any ) {
            logger.error( JSON.stringify( err?.message ?? err ) );
            if ( isCurrent() ) {
                // A timeout (the bare timeout string) or a dropped connection (TypeError) is transient: keep the
                // member's localStorage copy and selection on screen, as when goFetch resolved null for it. Only a
                // typed server answer (RuntimeException) clears the store.
                if ( err instanceof RuntimeException ) {
                    reset();
                }
                // After reset(), which clears it. What the member sees changes, so it is reported, not only logged.
                portfoliosLoad.fail( err );
                // goFetch has already reported an Unauthorized (session expiry) on its way out.
                if ( !( err instanceof Unauthorized ) ) {
                    clientStateManager.queueClientError( err, {} );
                }
            }
        } finally {
            // Clear the "loading" flag
            if ( isCurrent() ) {
                loading.value = false;
            }
        }
    }

    function setSelectedPortfolio( portfolioId: number ) {
        selectedPortfolioId.value = portfolioId;
    }

    function setSelectedPortfolioTitle( title: string ) {
        if ( selectedPortfolio.value ) {
            selectedPortfolio.value!.portfolio_name = title;
        }
    }

    function addNewPortfolio( portfolio: MyPortfolio ) {
        // Add the new portfolio to the in-memory collection
        allPortfolios.value.push( portfolio );
        // Select the newly added portfolio
        selectedPortfolioId.value = portfolio.portfolio_id;
    }

    async function refreshPortfolios() {
        // Capture the currently selected portfolio
        const portfolioId = selectedPortfolioId.value;
        // Clear all state for the current set of portfolios
        reset();
        // Re-load all data from the server
        await initialize();
        // Re-set the selected portfolio
        if ( !!portfolioId ) {
            setSelectedPortfolio( portfolioId );
        }
    }

    function removeSelectedPortfolio() {
        const portfolioId = selectedPortfolioId.value;
        if ( portfolioId && allPortfolios.value.length > 1 ) {
            const index = allPortfolios.value.findIndex( p => p.portfolio_id === portfolioId );
            allPortfolios.value.splice( index, 1 );
            selectedPortfolioId.value = allPortfolios.value[ 0 ].portfolio_id;
        }
    }

    function removeSelectedPositionFromPortfolio( position: MyPosition ) {
        const positions = selectedPortfolio.value?.positions;
        if ( positions ) {
            const index = positions.findIndex( p => p.pos_id === position.pos_id );
            positions.splice( index, 1 );
        }
    }

    return {
        // ACTIONS
        initialize,
        addNewPortfolio,
        refreshPortfolios,
        removeSelectedPortfolio,
        removeSelectedPositionFromPortfolio,
        reset,
        setSelectedPortfolio,
        setSelectedPortfolioTitle,

        // GETTERS
        allPortfolios,
        isLoading,
        loadError: portfoliosLoad.error,
        loadFailure: portfoliosLoad.failure,
        loadStatus: portfoliosLoad.status,
        selectedPortfolio
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useMyPortfoliosStore, import.meta.hot ) );
}